<script setup lang="ts">
import HamburgerIcon from '@assets/icons/hamburger.svg';
import JrnysLogo from '@assets/icons/jrnys-logo-new.svg';
import UserProfileIcon from '@assets/icons/user-profile.svg';
import PromoCode from '@components/PromoCode/PromoCode.vue';
import { getUserMenuItemsForSession } from '@helpers/user-menu';
import { useUserStore } from '@stores/index';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import MegaMenu from './MegaMenu.vue';
import MobileDrawer from './MobileDrawer.vue';
import SiteHeaderUserMenu from './SiteHeaderUserMenu.vue';
import type { NavItem } from './types';

interface Props {
  navItems?: NavItem[];
  logoColor?: 'white' | 'dark';
  promoText?: string;
  promoCode?: string;
  authLabel?: string;
  isLoggedIn?: boolean;
}

const userStore = useUserStore();

const props = withDefaults(defineProps<Props>(), {
  logoColor: 'white',
  promoText: '',
  promoCode: '',
  authLabel: 'Sign up / Log in',
  isLoggedIn: false,
  navItems: () => [
    {
      label: 'Weight Loss',
      href: '/weight-loss',
      children: [
        { label: 'View all', href: '/weight-loss' },
        { label: 'Munjaro', href: '/weight-loss/munjaro', icon: 'rx' },
        { label: 'Zepbound', href: '/weight-loss/zepbound', icon: 'rx' },
        { label: 'Ozempic', href: '/weight-loss/ozempic', icon: 'rx' },
        { label: 'Wegovy', href: '/weight-loss/wegovy', icon: 'rx' },
      ],
      cards: [
        {
          title: 'Discover weight loss treatments',
          image: '/dist/img/mega-menu-wl-discover.jpg',
          href: '/weight-loss',
        },
        {
          title: 'See if you are eligible for treatment',
          image: '/dist/img/mega-menu-wl-eligible.jpg',
          href: '/weight-loss/quiz',
        },
      ],
    },
    {
      label: 'Sexual Wellness',
      href: '/sexual-wellness',
      children: [
        { label: 'View all', href: '/sexual-wellness' },
      ],
      cards: [],
    },
    {
      label: 'About Us',
      href: '/about',
    },
    {
      label: 'How it Works',
      href: '/how-it-works',
    },
  ],
});

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'login'): void;
  (e: 'signup'): void;
}>();

const isMobileOpen = ref(false);
const activeMenuIndex = ref<number | null>(null);
const isScrolled = ref(false);
const isHidden = ref(false);
const currentPath = ref(typeof window !== 'undefined' ? window.location.pathname : '');
let lastScrollY = 0;
let scrollAccum = 0;
let closeTimeout: ReturnType<typeof setTimeout> | null = null;

const activeItem = computed(() => (activeMenuIndex.value !== null ? props.navItems[activeMenuIndex.value] : null));
const hasMegaContent = computed(() => (activeItem.value && (activeItem.value.children?.length || activeItem.value.cards?.length)));
const isMegaOpen = computed(() => !!hasMegaContent.value);

const isLightMenuPage = computed(() => {
  if (typeof document !== 'undefined' && document.body?.dataset?.transparentMarketingNav === '1') {
    return true;
  }
  const path = currentPath.value || '/';
  return path === '/';
});

/**
 * Marketing/light-menu pages: transparent by default (hero visible behind nav),
 * switch to solid only after scroll or when mobile drawer is open.
 * Non-marketing pages: always solid.
 */
const hasWhiteBg = computed(() => {
  if (!isLightMenuPage.value) return true;
  return isScrolled.value || isMobileOpen.value || isMegaOpen.value;
});
const hasShadow = computed(() => isScrolled.value || isMegaOpen.value);
const effectiveColor = computed(() => (hasWhiteBg.value ? 'dark' : 'white'));

const isActiveNavItem = (href: string) => {
  if (!href) return false;
  const path = currentPath.value;
  return path === href || (href !== '/' && path.startsWith(`${href}/`));
};

const handleScroll = () => {
  const currentY = window.scrollY;
  isScrolled.value = currentY > 50;

  if (isMegaOpen.value || isMobileOpen.value) {
    lastScrollY = currentY;
    return;
  }

  const delta = currentY - lastScrollY;

  if ((delta > 0 && scrollAccum < 0) || (delta < 0 && scrollAccum > 0)) {
    scrollAccum = 0;
  }
  scrollAccum += delta;

  if (scrollAccum > 20 && currentY > 100) {
    isHidden.value = true;
    scrollAccum = 0;
  } else if (scrollAccum < -3) {
    isHidden.value = false;
    scrollAccum = 0;
  }
  lastScrollY = currentY;
};

const loadSessionForMenu = async () => {
  if (!props.isLoggedIn) {
    return;
  }
  if (!userStore.info) {
    await userStore.getInfo();
  }
};

onMounted(async () => {
  currentPath.value = window.location.pathname;
  lastScrollY = window.scrollY;
  isScrolled.value = window.scrollY > 50;
  window.addEventListener('scroll', handleScroll, { passive: true });
  await loadSessionForMenu();
});

watch(() => props.isLoggedIn, (loggedIn) => {
  if (loggedIn) {
    loadSessionForMenu();
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const openMenu = (index: number) => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }
  activeMenuIndex.value = index;
};

const scheduleClose = () => {
  closeTimeout = setTimeout(() => {
    activeMenuIndex.value = null;
  }, 200);
};

const cancelClose = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }
};

const handleLogin = () => {
  isMobileOpen.value = false;
  emit('login');
};

const handleSignup = () => {
  isMobileOpen.value = false;
  emit('signup');
};

const userMenuItems = computed(() => {
  if (!props.isLoggedIn) {
    return [];
  }
  return getUserMenuItemsForSession(userStore.info);
});
</script>

<template>
  <header
    class="site-header fixed inset-x-0 top-0 z-30 font-inter"
    :class="[
      hasWhiteBg ? 'site-header--solid' : '',
      hasShadow ? 'site-header--shadow' : '',
      isHidden ? 'site-header--hidden' : ''
    ]"
    @mouseleave="scheduleClose"
  >
    <!-- Promo Bar -->
    <PromoCode
      v-if="promoText"
      :text="promoText"
      :code="promoCode"
    />

    <div class="container relative">
      <div class="flex h-[64px] items-center justify-between lg:h-85">
        <!-- Left: Mobile Hamburger / Desktop Logo -->
        <div class="flex shrink-0 items-center">
          <!-- Mobile: Hamburger -->
          <button
            type="button"
            class="flex size-36 items-center justify-center lg:hidden"
            aria-label="Open menu"
            @click="isMobileOpen = true"
          >
            <HamburgerIcon
              class="h-8 w-22"
              :class="effectiveColor === 'white' ? 'text-white' : 'text-deep-blue'"
            />
          </button>

          <!-- Desktop: Logo -->
          <a
            href="/"
            class="hidden lg:block"
          >
            <JrnysLogo
              class="h-16 w-[87px]"
              :class="effectiveColor === 'white' ? 'text-white' : 'text-deep-blue'"
            />
          </a>
        </div>

        <!-- Center: Mobile Logo / Desktop Navigation -->
        <a
          href="/"
          class="lg:hidden"
        >
          <JrnysLogo
            class="h-14 w-[76px]"
            :class="effectiveColor === 'white' ? 'text-white' : 'text-deep-blue'"
          />
        </a>

        <nav class="hidden lg:block">
          <ul class="flex items-center gap-4">
            <li
              v-for="(item, index) in navItems"
              :key="item.href"
              @mouseenter="openMenu(index)"
            >
              <a
                :href="item.href"
                class="site-header__nav-link relative block px-16 py-10 text-14 transition-all"
                :class="[
                  effectiveColor === 'white' ? 'text-white' : 'text-deep-blue',
                  (activeMenuIndex === index || (isActiveNavItem(item.href) && hasWhiteBg)) ? 'font-bold' : 'font-[450]',
                  isActiveNavItem(item.href) && hasWhiteBg ? 'site-header__nav-link--active' : ''
                ]"
              >
                {{ item.label }}
              </a>
            </li>
          </ul>
        </nav>

        <!-- Right: Profile menu dropdown when logged in, Login/Sign up when not -->
        <div class="flex shrink-0 items-center">
          <template v-if="isLoggedIn">
            <SiteHeaderUserMenu
              :items="userMenuItems"
              :icon-color="effectiveColor === 'white' ? 'white' : 'dark'"
              variant="mobile"
            />
            <SiteHeaderUserMenu
              :items="userMenuItems"
              :icon-color="effectiveColor === 'white' ? 'white' : 'dark'"
              variant="desktop"
            />
          </template>
          <template v-else>
            <button
              type="button"
              class="flex size-36 items-center justify-center lg:hidden"
              :class="effectiveColor === 'white' ? 'text-white' : 'text-deep-blue'"
              aria-label="Sign up / Log in"
              @click="$emit('login')"
            >
              <UserProfileIcon class="size-24" />
            </button>
            <button
              type="button"
              class="site-header__cta hidden items-center rounded-full px-24 py-10 text-14 font-semibold transition-colors lg:flex"
              :class="[
                effectiveColor === 'white'
                  ? 'bg-white text-deep-blue hover:bg-white-50'
                  : 'bg-deep-blue text-white hover:opacity-80'
              ]"
              @click="$emit('login')"
            >
              {{ authLabel }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Desktop: Mega Menu Panel (full-width, directly below header) -->
    <div
      class="mega-menu-wrapper absolute inset-x-0 top-full hidden overflow-hidden lg:block"
      @mouseenter="cancelClose"
      @mouseleave="scheduleClose"
    >
      <Transition name="mega-menu">
        <div
          v-if="hasMegaContent && activeItem"
        >
          <MegaMenu :item="activeItem" />
        </div>
      </Transition>
    </div>

    <!-- Mobile Drawer -->
    <MobileDrawer
      v-model="isMobileOpen"
      :nav-items="navItems"
      :is-logged-in="isLoggedIn"
      @login="handleLogin"
      @signup="handleSignup"
    />
  </header>
</template>

<style lang="scss" scoped>
.site-header {
  transform: translateY(0);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              background-color 0.3s ease,
              box-shadow 0.3s ease;
  will-change: transform;

  &--hidden {
    transform: translateY(-100%);
  }

  &--solid {
    background-color: #fff;
  }

  &--shadow {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  &__nav-link {
    text-decoration: none;
    border-radius: 9999px;

    &:hover {
      font-weight: 700;
    }

    &--active {
      background-color: #f0eeec;
      box-shadow: none;
    }
  }

}

/* Mega menu slide down / up */
.mega-menu-enter-active {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
}

.mega-menu-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s ease;
}

.mega-menu-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.mega-menu-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
